import type { NavOrderEntry, NavReorderItem, Report, ReportSidebarLayoutItem } from '@/api/generated/model';
import { PAGE_KEY_PREFIX, REPORT_KEY_PREFIX } from '@/domains/pages/constants';
import type {
  NebulyPageDefinition,
  NebulyPageKey,
  NebulyPageNavKey,
  WorkspaceLayoutEntry,
  WorkspaceLayoutSnapshot,
  WorkspaceNavItem,
  WorkspaceNavKey,
} from '@/domains/pages/types';
import { getReportSubproduct, type ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function toPageNavKey(pageKey: NebulyPageKey): NebulyPageNavKey {
  return `${PAGE_KEY_PREFIX}${pageKey}`;
}

export function toReportNavKey(reportId: string): `report:${string}` {
  return `${REPORT_KEY_PREFIX}${reportId}`;
}

export function isPageNavKey(key: string): key is NebulyPageNavKey {
  return key.startsWith(PAGE_KEY_PREFIX);
}

export function isReportNavKey(key: string): key is `report:${string}` {
  return key.startsWith(REPORT_KEY_PREFIX);
}

export function parsePageKeyFromNavKey(key: NebulyPageNavKey): NebulyPageKey {
  return key.slice(PAGE_KEY_PREFIX.length) as NebulyPageKey;
}

export function parseReportIdFromNavKey(key: `report:${string}`): string {
  return key.slice(REPORT_KEY_PREFIX.length);
}

/** Normalize legacy report UUIDs and prefixed keys into workspace nav keys. */
export function normalizeWorkspaceNavKey(raw: string): WorkspaceNavKey | null {
  if (isPageNavKey(raw)) return raw;
  if (isReportNavKey(raw)) return raw;
  if (UUID_RE.test(raw)) return toReportNavKey(raw);
  return null;
}

/** Keep only stored layout entries that still exist in the current page/report catalog. */
export function mergeWorkspaceLayoutWithCatalog(params: {
  stored: WorkspaceLayoutSnapshot | undefined;
  pages: NebulyPageDefinition[];
  workspaceReports: Report[];
}): WorkspaceLayoutSnapshot {
  const { stored, pages, workspaceReports } = params;
  if (!stored) {
    return { version: 0, order: [] };
  }

  const pageByKey = new Map(pages.map((page) => [page.navKey, page]));
  const reportById = new Map(workspaceReports.map((report) => [report.id, report]));
  const knownKeys = new Set<WorkspaceNavKey>();
  const mergedOrder: WorkspaceLayoutEntry[] = [];

  for (const entry of stored.order) {
    const normalized = normalizeWorkspaceNavKey(entry.key);
    if (!normalized || knownKeys.has(normalized)) continue;

    if (isPageNavKey(normalized)) {
      if (!pageByKey.has(normalized)) continue;
      knownKeys.add(normalized);
      mergedOrder.push({ key: normalized, enabled: entry.enabled });
      continue;
    }

    const reportId = parseReportIdFromNavKey(normalized);
    if (!reportById.has(reportId)) continue;
    knownKeys.add(normalized);
    mergedOrder.push({ key: normalized, enabled: entry.enabled });
  }

  return { version: stored.version, order: mergedOrder };
}

export function workspaceNavItemsFromLayout(params: {
  layout: WorkspaceLayoutSnapshot;
  pages: NebulyPageDefinition[];
  workspaceReports: Report[];
}): WorkspaceNavItem[] {
  const { layout, pages, workspaceReports } = params;
  const pageByKey = new Map(pages.map((page) => [page.navKey, page]));
  const reportById = new Map(workspaceReports.map((report) => [report.id, report]));
  const items: WorkspaceNavItem[] = [];

  for (const entry of layout.order) {
    if (isPageNavKey(entry.key)) {
      const page = pageByKey.get(entry.key);
      if (!page) continue;
      items.push({ kind: 'page', key: entry.key, page });
      continue;
    }

    if (isReportNavKey(entry.key)) {
      const report = reportById.get(parseReportIdFromNavKey(entry.key));
      if (!report) continue;
      items.push({ kind: 'report', key: entry.key, report });
    }
  }

  return items;
}

export function getWorkspaceHiddenNavKeys(layout: WorkspaceLayoutSnapshot): Set<WorkspaceNavKey> {
  return new Set(layout.order.filter((entry) => !entry.enabled).map((entry) => entry.key));
}

export function buildWorkspaceLayoutPayload(
  orderedItems: WorkspaceNavItem[],
  hiddenKeys: Set<WorkspaceNavKey>,
  version: number,
): WorkspaceLayoutSnapshot {
  return {
    version,
    order: orderedItems.map((item) => ({
      key: item.key,
      enabled: !hiddenKeys.has(item.key),
    })),
  };
}

export function inferNavOrderEntrySubproduct(
  entry: NavOrderEntry,
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): ReportSubproduct | undefined {
  if (entry.subproduct) return entry.subproduct;

  const normalized = normalizeWorkspaceNavKey(entry.key);
  if (!normalized) return undefined;

  if (isPageNavKey(normalized)) {
    return pages.find((page) => page.navKey === normalized)?.subproduct;
  }

  const report = workspaceReports.find((row) => row.id === parseReportIdFromNavKey(normalized));
  return report ? getReportSubproduct(report) : undefined;
}

export function navOrderEntryBelongsToSubproduct(
  entry: NavOrderEntry,
  subproduct: ReportSubproduct,
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): boolean {
  return inferNavOrderEntrySubproduct(entry, pages, workspaceReports) === subproduct;
}

export function filterNavOrderBySubproduct(
  order: NavOrderEntry[],
  subproduct: ReportSubproduct,
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): NavOrderEntry[] {
  return order.filter((entry) => navOrderEntryBelongsToSubproduct(entry, subproduct, pages, workspaceReports));
}

export function mergeSubproductOrderIntoFullOrder(
  fullOrder: NavOrderEntry[],
  subproduct: ReportSubproduct,
  subproductOrder: NavOrderEntry[],
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): NavOrderEntry[] {
  const otherEntries = fullOrder.filter(
    (entry) => !navOrderEntryBelongsToSubproduct(entry, subproduct, pages, workspaceReports),
  );
  const mergedSubproductOrder = subproductOrder.map((entry) => ({
    ...entry,
    subproduct,
  }));
  return [...otherEntries, ...mergedSubproductOrder];
}

export function layoutSnapshotFromApiOrder(
  version: number,
  order: NavOrderEntry[],
  subproduct: ReportSubproduct,
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): WorkspaceLayoutSnapshot {
  const mergedOrder: WorkspaceLayoutEntry[] = [];
  const knownKeys = new Set<WorkspaceNavKey>();

  for (const entry of filterNavOrderBySubproduct(order, subproduct, pages, workspaceReports)) {
    const normalized = normalizeWorkspaceNavKey(entry.key);
    if (!normalized || knownKeys.has(normalized)) continue;
    knownKeys.add(normalized);
    mergedOrder.push({ key: normalized, enabled: entry.enabled !== false });
  }

  return { version, order: mergedOrder };
}

export function layoutSnapshotFromFullApiOrder(
  version: number,
  order: NavOrderEntry[],
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): WorkspaceLayoutSnapshot {
  const mergedOrder: WorkspaceLayoutEntry[] = [];
  const knownKeys = new Set<WorkspaceNavKey>();

  for (const entry of order) {
    const normalized = normalizeWorkspaceNavKey(entry.key);
    if (!normalized || knownKeys.has(normalized)) continue;

    if (isPageNavKey(normalized)) {
      if (!pages.some((page) => page.navKey === normalized)) continue;
    } else if (isReportNavKey(normalized)) {
      const reportId = parseReportIdFromNavKey(normalized);
      if (!workspaceReports.some((report) => report.id === reportId)) continue;
    } else {
      continue;
    }

    knownKeys.add(normalized);
    mergedOrder.push({ key: normalized, enabled: entry.enabled !== false });
  }

  return { version, order: mergedOrder };
}

export function getNavItemSubproduct(item: WorkspaceNavItem): ReportSubproduct {
  return item.kind === 'page' ? item.page.subproduct : getReportSubproduct(item.report);
}

export function filterNavItemsBySubproduct(
  items: WorkspaceNavItem[],
  subproduct: ReportSubproduct,
): WorkspaceNavItem[] {
  return items.filter((item) => getNavItemSubproduct(item) === subproduct);
}

export function resolveWorkspaceNavKeySubproduct(
  navKey: WorkspaceNavKey,
  pages: NebulyPageDefinition[],
  workspaceReports: Report[],
): ReportSubproduct | null {
  const subproduct = inferNavOrderEntrySubproduct({ key: navKey, enabled: true }, pages, workspaceReports);
  return subproduct ?? null;
}

export function buildSubproductLayoutPayload(
  orderedItems: WorkspaceNavItem[],
  hiddenKeys: Set<WorkspaceNavKey>,
  version: number,
  subproduct: ReportSubproduct,
): WorkspaceLayoutSnapshot {
  const scopedItems = filterNavItemsBySubproduct(orderedItems, subproduct);
  const scopedHidden = new Set(
    [...hiddenKeys].filter((key) => scopedItems.some((item) => item.key === key)),
  );
  return buildWorkspaceLayoutPayload(scopedItems, scopedHidden, version);
}

export function toNavReorderItems(
  layout: WorkspaceLayoutSnapshot,
  subproduct: ReportSubproduct,
): NavReorderItem[] {
  return layout.order.map((entry) => ({
    key: entry.key,
    enabled: entry.enabled,
    subproduct,
  }));
}

export function layoutOrderFromPutItems(items: ReportSidebarLayoutItem[]): NavOrderEntry[] {
  return items.map((item) => ({
    key: item.key,
    enabled: !item.sidebar_hidden,
  }));
}

export function getNavItemPersistId(item: WorkspaceNavItem): string {
  return item.kind === 'page' ? item.key : item.report.id;
}

/** Unique id for React keys and drag lists (opaque nav key for pages, report UUID for reports). */
export function getNavItemId(item: WorkspaceNavItem): string {
  return item.key;
}

export function getNavItemLabel(item: WorkspaceNavItem): string {
  return item.kind === 'page' ? item.page.title : item.report.name;
}
